import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronRight, Calendar, Leaf, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get current month name
const getCurrentMonth = () => {
  return new Date().toLocaleString('default', { month: 'long' });
};

// Check if a product is currently in season
const isInSeason = (seasonStart, seasonEnd) => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                  'July', 'August', 'September', 'October', 'November', 'December'];
  const currentMonth = new Date().getMonth();
  const startIdx = months.indexOf(seasonStart);
  const endIdx = months.indexOf(seasonEnd);
  
  if (startIdx <= endIdx) {
    return currentMonth >= startIdx && currentMonth <= endIdx;
  } else {
    // Season spans year boundary (e.g., November - March)
    return currentMonth >= startIdx || currentMonth <= endIdx;
  }
};

// Seasonal Product Card
const SeasonalCard = ({ product, onFilter, currencySymbol }) => {
  const inSeason = isInSeason(product.season_start, product.season_end);
  
  return (
    <Card 
      className={`group hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden ${
        inSeason ? 'ring-2 ring-green-500 ring-offset-2' : 'opacity-80'
      }`}
      onClick={() => onFilter(product)}
      data-testid={`seasonal-card-${product.id}`}
    >
      {/* Image Header */}
      <div className="relative h-32 overflow-hidden">
        <img 
          src={product.image_url} 
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Season Badge */}
        <div className="absolute top-2 right-2">
          {inSeason ? (
            <Badge className="bg-green-500 text-white animate-pulse">
              {product.highlight_text || 'In Season'}
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-white/90">
              {product.season_start} - {product.season_end}
            </Badge>
          )}
        </div>
        
        {/* Icon */}
        <div className="absolute bottom-2 left-2 text-3xl drop-shadow-lg">
          {product.icon}
        </div>
      </div>
      
      {/* Content */}
      <CardContent className="p-3">
        <h3 className="font-semibold text-sm line-clamp-1 mb-1">{product.title}</h3>
        <p className="text-xs text-gray-500 line-clamp-2 mb-2">{product.description}</p>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <Calendar className="w-3 h-3" />
            <span>{product.season_start} - {product.season_end}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors" />
        </div>
      </CardContent>
    </Card>
  );
};

export default function WhatsInSeasonSection({ 
  country = 'UK', 
  onFilterByTags,
  className = '' 
}) {
  const [seasonalData, setSeasonalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';

  useEffect(() => {
    const fetchSeasonalProducts = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/food-agriculture/seasonal-products/${country}`
        );
        const data = await response.json();
        
        if (data.success) {
          setSeasonalData(data);
        }
      } catch (error) {
        console.error('Failed to fetch seasonal products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSeasonalProducts();
  }, [country]);

  const handleFilter = (product) => {
    if (onFilterByTags) {
      onFilterByTags(product.filter_tags, product.subcategory_slug);
    }
  };

  // Don't render if disabled or no products
  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <div className="h-6 w-48 bg-gray-200 animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-100 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!seasonalData?.enabled || !seasonalData?.products?.length) {
    return null;
  }

  // Sort products: in-season first, then by display order
  const sortedProducts = [...seasonalData.products].sort((a, b) => {
    const aInSeason = isInSeason(a.season_start, a.season_end);
    const bInSeason = isInSeason(b.season_start, b.season_end);
    
    if (aInSeason && !bInSeason) return -1;
    if (!aInSeason && bInSeason) return 1;
    return a.display_order - b.display_order;
  });

  const displayProducts = showAll ? sortedProducts : sortedProducts.slice(0, 5);
  const inSeasonCount = sortedProducts.filter(p => isInSeason(p.season_start, p.season_end)).length;

  return (
    <div className={`${className}`} data-testid="whats-in-season-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              {seasonalData.section_title}
              {inSeasonCount > 0 && (
                <Badge className="bg-green-100 text-green-700 text-xs">
                  {inSeasonCount} in season now
                </Badge>
              )}
            </h2>
            <p className="text-sm text-gray-500">{seasonalData.section_subtitle}</p>
          </div>
        </div>
        
        {sortedProducts.length > 5 && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setShowAll(!showAll)}
            className="text-green-600 hover:text-green-700"
          >
            {showAll ? 'Show Less' : `View All (${sortedProducts.length})`}
            <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showAll ? 'rotate-90' : ''}`} />
          </Button>
        )}
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {displayProducts.map((product) => (
          <SeasonalCard 
            key={product.id}
            product={product}
            onFilter={handleFilter}
            currencySymbol={currencySymbol}
          />
        ))}
      </div>

      {/* Quick Filter Hint */}
      <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
        <Filter className="w-3 h-3" />
        <span>Click any card to filter listings by seasonal products</span>
      </div>
    </div>
  );
}
